// When a web screen inside the iPhone app has drawn, and pull to refresh
// (src/native/embedded.ts, the `boot` / `rendered` messages and
// `latticeNative.refresh`). The app keeps its own placeholder over a
// transparent web view until the page says `rendered`, so neither the
// boot splash nor a screen's own loading state shows inside the app.
//
// "Drawn" = the companion screen's route has loaded (no EmbeddedPending
// mounted) and no query is fetching, for RENDER_QUIET_MS, then a paint;
// or RENDER_CAP_MS after the shell mounted, whatever is still loading.
// Sent once per page load: later in-page navigations show as they are.

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { exposeToNative, postToNative, renderSettled } from "./embedded";

const TICK_MS = 40;

// Route fallbacks on screen (EmbeddedPending), and whether this page load
// has said `rendered` already (module state = one page load).
let pending = 0;
let told = false;

/** Runs `fn` once the frame after next has painted (or soon, where rAF never fires: a hidden web view). */
function afterPaint(fn: () => void): void {
  let done = false;
  const fire = () => {
    if (done) return;
    done = true;
    fn();
  };
  window.requestAnimationFrame(() => window.requestAnimationFrame(fire));
  window.setTimeout(fire, 150);
}

/**
 * In the embedded shell: tell the app when the screen has drawn with its
 * data, and let its pull to refresh fetch that data again in place.
 */
export function useNativeRendered(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    exposeToNative({
      refresh: async () => {
        await queryClient.refetchQueries({ type: "active" });
        return true;
      },
    });
  }, [queryClient]);

  useEffect(() => {
    if (told) return;
    const start = performance.now();
    let quietSince: number | null = null;
    let timer = 0;
    let stopped = false;
    const tick = () => {
      if (stopped || told) return;
      const now = performance.now();
      const busy = pending > 0 || queryClient.isFetching() > 0;
      if (busy) quietSince = null;
      else if (quietSince === null) quietSince = now;
      if (renderSettled(now - start, busy, quietSince === null ? 0 : now - quietSince)) {
        afterPaint(() => {
          if (told) return;
          told = true;
          postToNative({ type: "rendered" });
        });
        return;
      }
      timer = window.setTimeout(tick, TICK_MS);
    };
    timer = window.setTimeout(tick, TICK_MS);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [queryClient]);
}

/**
 * A route still loading, inside the app: nothing to see (the app's
 * placeholder covers the first load), and it holds `rendered` back.
 */
export function EmbeddedPending() {
  useEffect(() => {
    pending += 1;
    return () => {
      pending -= 1;
    };
  }, []);
  return <div className="h-full w-full" aria-busy="true" />;
}
